// src/features/dashboard/hooks/useDashboardData.ts

import { useState, useEffect } from 'react';
import api, { dealsApi, projectsApi, casesApi, customersApi, projectRequestsApi } from '../../../services/api';
import { DealListItem, ProjectListItem, ProjectCase, Customer, ProjectRequestListItem } from '../../../types';
import { DashboardStats } from '../types';
import { calculateDashboardStats } from '../utils/dashboardHelpers';

interface UseDashboardDataReturn {
  stats: DashboardStats;
  recentProjectRequests: ProjectRequestListItem[];
  recentDeals: DealListItem[];
  recentProjects: ProjectListItem[];
  recentCases: ProjectCase[];
  loading: boolean;
  error: string | null;
}

// Custom hook to fetch and process all dashboard data
export function useDashboardData(): UseDashboardDataReturn {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [stats, setStats] = useState<DashboardStats>({
    projectRequests: { total: 0, pending: 0, converted: 0 },
    deals: { total: 0, byStage: {}, closedWon: 0, closedLost: 0 },
    projects: { total: 0, active: 0, completed: 0 },
    cases: { total: 0, open: 0, resolved: 0 },
    customers: { total: 0, active: 0 },
    tasks: { total: 0, inProgress: 0, completed: 0 },
  });
  const [recentProjectRequests, setRecentProjectRequests] = useState<ProjectRequestListItem[]>([]);
  const [recentDeals, setRecentDeals] = useState<DealListItem[]>([]);
  const [recentProjects, setRecentProjects] = useState<ProjectListItem[]>([]);
  const [recentCases, setRecentCases] = useState<ProjectCase[]>([]);

  useEffect(() => {
    let mounted = true;

    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);

        const [projectRequestsRes, dealsRes, projectsRes, casesRes, customersRes] = await Promise.all([
          projectRequestsApi.list({ page: 1 }),
          api.get('/deals', { params: { page: 1, per_page: 100 } }),
          projectsApi.list({ page: 1 }),
          casesApi.list({ page: 1 }),
          customersApi.getAll({ per_page: 100, status: 'active' }),
        ]);

        if (!mounted) return;

        const projectRequests = (projectRequestsRes as any).data?.data || (projectRequestsRes as any).data || [];
        const deals = (dealsRes as any).data?.data || (dealsRes as any).data || [];
        const projects = (projectsRes as any).data?.data || (projectsRes as any).data || [];
        const cases = (casesRes as any).data?.data || (casesRes as any).data || [];
        const customers = (customersRes as any).data?.data || (customersRes as any).data || [];

        const calculatedStats = calculateDashboardStats(
          projectRequests,
          deals,
          projects,
          cases,
          customers
        );

        setStats(calculatedStats);
        setRecentProjectRequests(projectRequests.slice(0, 5));
        setRecentDeals(deals.slice(0, 5));
        setRecentProjects(projects.slice(0, 5));
        setRecentCases(cases.slice(0, 5));
      } catch (err: any) {
        if (mounted) {
          setError(err?.message || 'Failed to load dashboard data');
          console.error('Failed to load dashboard data:', err);
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    loadData();

    return () => {
      mounted = false;
    };
  }, []);

  return {
    stats,
    recentProjectRequests,
    recentDeals,
    recentProjects,
    recentCases,
    loading,
    error,
  };
}
